import { Outlet, Link, NavLink, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import api from "@/lib/api";
import WhatsAppFab from "@/components/WhatsAppFab";
import { useWebsite } from "@/lib/website";

const nav = [
  { to: "/how-it-works", label: "How It Works" },
  { to: "/packages", label: "Packages" },
  { to: "/ai-and-human-review", label: "AI + Human Review" },
  { to: "/kdp-publishing", label: "KDP Publishing" },
  { to: "/faq", label: "FAQ" },
  { to: "/pages/aboutus", label: "About Us" },
];

export default function Layout({ children }) {
  const { content, preview } = useWebsite();
  const [params] = useSearchParams();
  const faqParams = new URLSearchParams(params);
  faqParams.set("page", "__faq");
  const faqDestination = preview ? `/admin/website-preview?${faqParams.toString()}` : "/faq";
  const aboutParams = new URLSearchParams(params);
  aboutParams.set("page", "aboutus");
  const hasAboutPage = content.pages?.some(page => page.slug === "aboutus");
  const aboutDestination = hasAboutPage
    ? (preview ? `/admin/website-preview?${aboutParams.toString()}` : "/pages/aboutus")
    : "https://bookghostpublishing.com/pages/aboutus";
  const navDestination = to => to === "/faq" ? faqDestination : to === "/pages/aboutus" ? aboutDestination : to;
  const navActive = (to, isActive) => {
    if (preview && to === "/faq") return params.get("page") === "__faq";
    if (preview && to === "/pages/aboutus") return params.get("page") === "aboutus";
    return isActive;
  };
  const contact = content.contact;
  const announce = contact.announcement;
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({});
  useEffect(() => {
    api.get("/settings/public").then(r => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <div className="flex min-h-screen flex-col">
      {announce && (
        <div className="bg-[#1B2A4A] text-[#F6F3EC] text-center text-sm py-2 px-4" data-testid="announcement-banner">
          {announce}
        </div>
      )}
      <header className="sticky top-0 z-50 bg-[#F6F3EC]/95 backdrop-blur border-b border-[#D8D2C4]">
        <div className="container-narrow flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="brand-logo" aria-label="BookGhost Publishing home">
            <img src="/brand/bookghost-logo.png" alt="BookGhost Publishing" className="h-16 w-auto rounded-sm" data-testid="header-logo-img" />
          </Link>
          <nav className="hidden xl:flex items-center gap-4">
            {nav.map(n => (
              <NavLink key={n.to} to={navDestination(n.to)} data-testid={`nav-${n.to.slice(1)}`}
                className={({ isActive }) => `text-sm whitespace-nowrap tracking-wide ${navActive(n.to, isActive) ? "text-[#1B2A4A] gold-underline" : "text-[#3A3A3A] hover:text-[#1B2A4A]"}`}>
                {n.label}
              </NavLink>
            ))}
            <Link to="/discuss-my-book#enquiry" data-testid="header-cta-start"
              className="inline-flex items-center whitespace-nowrap bg-[#1B2A4A] hover:bg-[#142038] text-[#F6F3EC] px-5 py-2.5 rounded-md text-sm font-medium">
              Discuss My Book Idea
            </Link>
          </nav>
          <button onClick={() => setOpen(!open)} className="xl:hidden text-[#1B2A4A]" aria-label="Toggle menu" aria-expanded={open}
            data-testid="mobile-menu-toggle">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {open && (
          <div className="xl:hidden border-t border-[#D8D2C4] bg-[#F6F3EC]">
            <div className="container-narrow py-4 flex flex-col gap-4">
              {nav.map(n => (
                <NavLink key={n.to} to={navDestination(n.to)} onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.to.slice(1)}`}
                  className="text-base text-[#1B2A4A] py-1">{n.label}</NavLink>
              ))}
              <Link to="/discuss-my-book#enquiry" onClick={() => setOpen(false)} data-testid="mobile-cta-start"
                className="inline-flex items-center justify-center bg-[#1B2A4A] text-[#F6F3EC] px-5 py-3 rounded-md text-sm font-medium">
                Discuss My Book Idea
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        {children || <Outlet />}
      </main>

      <footer className="bg-[#1B2A4A] text-[#F6F3EC]/90 mt-16">
        <div className="container-narrow py-14 grid gap-10 md:grid-cols-4">
          <div className="md:col-span-2">
            <div className="mb-4 inline-block bg-[#FDFCF9] rounded-lg p-2">
              <img src="/brand/bookghost-logo.png" alt="BookGhost Publishing" className="h-24 w-auto" data-testid="footer-logo-img" />
            </div>
            <p className="text-sm text-[#F6F3EC]/70 max-w-md" data-testid="footer-description">{contact.footer_text}</p>
            <p className="text-sm mt-4"><a className="hover:text-[#C7A25C]" href={`mailto:${contact.email}`} data-testid="footer-email">{contact.email}</a></p>
          </div>
          <div>
            <p className="font-serif text-lg mb-3">Explore</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/packages" className="hover:text-[#C7A25C]">Packages</Link></li>
              <li><Link to="/how-it-works" className="hover:text-[#C7A25C]">How It Works</Link></li>
              <li><Link to="/ai-and-human-review" className="hover:text-[#C7A25C]">AI + Human Review</Link></li>
              <li><Link to="/kdp-publishing" className="hover:text-[#C7A25C]">KDP Publishing</Link></li>
              <li><Link to={faqDestination} data-testid="footer-nav-faq" className="hover:text-[#C7A25C]">FAQ</Link></li>
              <li><Link to={aboutDestination} data-testid="footer-nav-about" className="hover:text-[#C7A25C]">About Us</Link></li>
              <li><Link to="/order-status" className="hover:text-[#C7A25C]">Order Status</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-serif text-lg mb-3">Legal</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/privacy-policy" className="hover:text-[#C7A25C]">Privacy Policy</Link></li>
              <li><Link to="/terms-and-conditions" className="hover:text-[#C7A25C]">Terms & Conditions</Link></li>
              <li><Link to="/refund-policy" className="hover:text-[#C7A25C]">Refund Policy</Link></li>
              <li><Link to="/contact" className="hover:text-[#C7A25C]">Contact</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[#F6F3EC]/10 py-6 text-center text-xs text-[#F6F3EC]/60">
          <p data-testid="footer-tax-notice">Prices in INR. Applicable tax treatment and final payable total are confirmed in the proposal before payment.</p>
          {(settings.legal_name || settings.city_state) && (
            <p className="mt-1">{settings.legal_name}{settings.legal_name && settings.city_state ? " · " : ""}{settings.city_state}</p>
          )}
          <p className="mt-1">© {new Date().getFullYear()} BookGhost Publishing. All rights reserved.</p>
        </div>
      </footer>
      <WhatsAppFab />
    </div>
  );
}
